import { io } from 'socket.io-client';

// Remove /api suffix from VITE_API_URL for WebSocket connection
const API_URL = (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace(/\/api$/, '');

class SocketService {
  constructor() {
    this.socket = null;
    this.listeners = new Map();
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.reconnectDelay = 1000; // Start with 1 second
    this.maxReconnectDelay = 30000; // Max 30 seconds
    this.tokenGetter = null; // Function to get fresh token
  }

  // Set a function that returns a fresh token (for reconnection)
  setTokenGetter(getter) {
    this.tokenGetter = getter;
  }

  connect(token) {
    // If socket exists (connected or not), don't create a new one
    // Let socket.io's built-in reconnection handle it - this preserves listeners
    if (this.socket) {
      if (this.socket.connected) {
        console.log('Socket already connected');
      } else {
        console.log('Socket exists but disconnected, will auto-reconnect');
      }
      return this.socket;
    }

    console.log('🔌 Connecting to WebSocket server...');

    // Use a function for auth so we can get fresh tokens on reconnect
    const self = this;
    this.socket = io(API_URL, {
      auth: async (cb) => {
        // Try to get fresh token, fall back to initial token
        let freshToken = token;
        if (self.tokenGetter) {
          try {
            freshToken = await self.tokenGetter();
          } catch (e) {
            console.warn('Failed to get fresh token, using initial token');
          }
        }
        cb({ token: freshToken });
      },
      transports: ['websocket', 'polling'],
      reconnection: true,
      reconnectionAttempts: this.maxReconnectAttempts,
      reconnectionDelay: this.reconnectDelay,
      reconnectionDelayMax: this.maxReconnectDelay,
      timeout: 10000,
      autoConnect: true
    });

    this.setupEventHandlers();
    return this.socket;
  }

  setupEventHandlers() {
    if (!this.socket) return;

    this.socket.on('connect', () => {
      console.log('✅ WebSocket connected:', this.socket.id);
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000; // Reset delay on successful connection

      // Notify all listeners of connection
      this.emitLocal('socket:connected', { socketId: this.socket.id });
    });

    this.socket.on('connected', (data) => {
      console.log('✅ Server confirmed connection:', data);
    });

    this.socket.on('disconnect', (reason) => {
      console.log('❌ WebSocket disconnected:', reason);
      this.emitLocal('socket:disconnected', { reason });
    });

    this.socket.on('connect_error', (error) => {
      console.error('❌ Connection error:', error.message);
      this.reconnectAttempts++;

      // Exponential backoff
      if (this.reconnectAttempts > 0) {
        this.reconnectDelay = Math.min(
          this.reconnectDelay * 1.5,
          this.maxReconnectDelay
        );
      }

      this.emitLocal('socket:error', { error: error.message, attempts: this.reconnectAttempts });
    });

    this.socket.on('reconnect', (attemptNumber) => {
      console.log('🔄 Reconnected after', attemptNumber, 'attempts');
      this.reconnectAttempts = 0;
      this.reconnectDelay = 1000;
      this.emitLocal('socket:reconnected', { attemptNumber });
    });

    this.socket.on('reconnect_attempt', (attemptNumber) => {
      console.log(`🔄 Reconnection attempt ${attemptNumber}...`);
    });

    this.socket.on('reconnect_failed', () => {
      console.error('❌ Reconnection failed after max attempts');
      this.emitLocal('socket:reconnect_failed', {});
    });
  }

  // Subscribe to an event
  on(event, callback) {
    if (!this.socket) {
      console.warn('Socket not initialized. Call connect() first.');
      return;
    }

    // Store callback for internal tracking
    if (!this.listeners.has(event)) {
      this.listeners.set(event, []);
    }
    this.listeners.get(event).push(callback);

    // Register with socket.io
    this.socket.on(event, callback);
  }

  // Unsubscribe from an event
  off(event, callback) {
    if (!this.socket) return;

    this.socket.off(event, callback);

    // Remove from internal tracking
    if (this.listeners.has(event)) {
      const callbacks = this.listeners.get(event);
      const index = callbacks.indexOf(callback);
      if (index > -1) {
        callbacks.splice(index, 1);
      }
      if (callbacks.length === 0) {
        this.listeners.delete(event);
      }
    }
  }

  // Emit an event to server
  emit(event, data) {
    if (!this.socket?.connected) {
      console.warn('Cannot emit - socket not connected');
      return;
    }
    this.socket.emit(event, data);
  }

  // Emit an event to local listeners only (not to server)
  emitLocal(event, data) {
    if (!this.listeners.has(event)) return;

    const callbacks = this.listeners.get(event);
    callbacks.forEach(callback => {
      try {
        callback(data);
      } catch (error) {
        console.error(`Error in listener for ${event}:`, error);
      }
    });
  }

  // Disconnect socket
  disconnect() {
    if (this.socket) {
      console.log('🔌 Disconnecting WebSocket...');

      // Remove all listeners
      this.listeners.forEach((callbacks, event) => {
        callbacks.forEach(callback => {
          this.socket.off(event, callback);
        });
      });
      this.listeners.clear();

      this.socket.disconnect();
      this.socket = null;
    }
  }

  // Check connection status
  isConnected() {
    return this.socket?.connected || false;
  }

  // Get socket instance (use sparingly)
  getSocket() {
    return this.socket;
  }
}

// Export singleton instance
const socketService = new SocketService();
export default socketService;
